import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/dateFormat';
import axios from 'axios';
import OwnerPageLayout from './layout/OwnerPageLayout';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchoolId } from '../utils/activeSchool';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { postWithCrossSchoolLink } from '../utils/crossSchoolDuplicate';
import { isCoachAccount } from '../utils/roles';
import LinkGuardianModal from './LinkGuardianModal';
import GuardianRelationshipSelect from './GuardianRelationshipSelect';
import { DEFAULT_GUARDIAN_RELATIONSHIP, guardianRelationshipLabel } from '../utils/guardianRelationships';

const API = getApiBaseUrl();

const canManageSubscriptions = () => {
    const r = localStorage.getItem('role') || '';
    return r === 'Owner' || r === 'Admin';
};

export default function StudentList() {
    const { t } = useTranslation(['students', 'common', 'errors', 'messages', 'groups']);
    const coachView = isCoachAccount();
    const [students, setStudents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState('');
    const [listTab, setListTab] = useState('active');
    const [enrollGroupId, setEnrollGroupId] = useState('');
    const [schoolId, setSchoolId] = useState(null);
    const [savingGroup, setSavingGroup] = useState(false);
    const [editGroupId, setEditGroupId] = useState(null);
    const [linkGuardianFor, setLinkGuardianFor] = useState(null);

    const [form, setForm] = useState({
        fullName: '', birthDate: '', phone: '', contactEmail: '', medicalNote: '',
        addGuardian: false, guardianPhone: '', guardianRelationship: DEFAULT_GUARDIAN_RELATIONSHIP
    });
    const [guardianLookup, setGuardianLookup] = useState(null);

    const [groupForm, setGroupForm] = useState({
        name: '', scheduleJSON: ''
    });

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });

    const inputStyle = {
        width: '100%', padding: '11px 14px', borderRadius: '10px',
        border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
        outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
        fontFamily: 'inherit'
    };

    useEffect(() => { loadData(); }, []);

    async function loadData() {
        setLoading(true);
        try {
            const schoolRes = await getSchoolForSession(api);
            const sid = getSchoolIdFromResponse(schoolRes) || getActiveSchoolId();
            setSchoolId(sid);
            const [studRes, groupRes] = await Promise.all([
                api.get(`/student?schoolId=${sid}`, {
                    params: coachView ? { includeArchived: false } : {}
                }),
                api.get(`/school/${sid}/groups`)
            ]);
            setStudents(studRes.data);
            setGroups(groupRes.data);
        } catch (e) {
            const msg = e.response?.status === 403
                ? t('students:noAccessCoach')
                : (e.response?.data?.message || t('students:loadError'));
            alert(msg);
        }
        setLoading(false);
    }

    async function handleAddStudent(e) {
        e.preventDefault();
        if (!schoolId) return alert(t('students:noSchool'));
        try {
            const body = {
                fullName: form.fullName,
                birthDate: form.birthDate || null,
                phone: form.phone || null,
                contactEmail: form.contactEmail || null,
                medicalNote: form.medicalNote || null,
                guardian: form.addGuardian && form.guardianPhone.trim()
                    ? {
                        phone: form.guardianPhone.trim(),
                        relationship: form.guardianRelationship
                    }
                    : null
            };
            const res = await postWithCrossSchoolLink(
                api, '/student', body, { schoolId },
                { linkParam: 'linkFromStudentId', idKey: 'studentId', entityLabel: t('students:entityStudent') }
            );
            if (!res) return;
            setShowForm(false);
            setForm({
                fullName: '', birthDate: '', phone: '', contactEmail: '', medicalNote: '',
                addGuardian: false, guardianPhone: '', guardianRelationship: DEFAULT_GUARDIAN_RELATIONSHIP
            });
            if (res.data?.linkedFromOtherSchool) {
                alert(t('students:linkedSuccess'));
            } else if (res.data?.guardianWarning) {
                alert(t('students:guardianWarning', { message: res.data.guardianWarning }));
            }
            loadData();
        } catch (err) {
            alert(err.message || err.response?.data?.message || t('students:addError'));
        }
    }

    async function handleCreateGroup(e) {
        e.preventDefault();
        setSavingGroup(true);
        try {
            if (editGroupId) {
                // Редактирование существующей группы
                await api.put(`/school/${schoolId}/groups/${editGroupId}`, {
                    name: groupForm.name,
                    coachId: null,
                    scheduleJSON: groupForm.scheduleJSON || null
                });
            } else {
                // Создание новой группы
                await api.post(`/school/${schoolId}/groups`, {
                    name: groupForm.name,
                    coachId: null,
                    scheduleJSON: groupForm.scheduleJSON || null
                });
            }
            setShowGroupForm(false);
            setEditGroupId(null);
            setGroupForm({ name: '', scheduleJSON: '' });
            loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('students:groupSaveError'));
        }
        setSavingGroup(false);
    }

    async function handleEnroll(studentId) {
        if (!enrollGroupId) return alert(t('students:pickGroup'));
        try {
            await api.post(`/student/${studentId}/enroll`, { groupId: parseInt(enrollGroupId) });
            setSelected(null);
            loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('students:enrollError'));
        }
    }

    async function handleDismiss(studentId) {
        const reason = window.prompt(t('students:expelReason'));
        if (reason === null) return;
        try {
            await api.post(`/student/${studentId}/dismiss`, { reason });
            loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('students:genericError'));
        }
    }

    async function handleArchive(studentId, name) {
        if (!window.confirm(t('students:archiveConfirm', { name })))
            return;
        try {
            await api.post(`/student/${studentId}/archive`);
            loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('students:archiveError'));
        }
    }

    async function handleRestore(studentId) {
        try {
            await api.post(`/student/${studentId}/restore`);
            loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('students:restoreError'));
        }
    }

    const matchesSearch = (s) =>
        s.fullName.toLowerCase().includes(search.toLowerCase()) ||
        (s.phone || s.parentPhone || '').includes(search)
        || (s.guardians || []).some(g =>
            (g.guardianPhone || '').includes(search) || (g.guardianName || '').includes(search));

    const activeStudents = students.filter(s =>
        s.status !== 'archived' && matchesSearch(s)
    );
    const archivedStudents = students.filter(s =>
        s.status === 'archived' && matchesSearch(s)
    );
    const filtered = listTab === 'archive' ? archivedStudents : activeStudents;

    const pageSubtitle = coachView
        ? t('students:statsCoach', { count: activeStudents.length, groups: groups.length })
        : t('students:statsOwner', {
            active: activeStudents.length,
            archived: archivedStudents.length > 0 ? t('students:archivedSuffix', { count: archivedStudents.length }) : '',
            groups: groups.length
        });

    const pageActions = !coachView ? (
        <>
            <button
                type="button"
                className={showGroupForm ? 'sp-btn sp-btn--outline' : 'sp-btn sp-btn--ghost'}
                onClick={() => {
                    setShowGroupForm(!showGroupForm);
                    setShowForm(false);
                    setEditGroupId(null);
                    setGroupForm({ name: '', scheduleJSON: '' });
                }}
            >
                {showGroupForm ? t('students:cancel') : t('students:addGroup')}
            </button>
            <button
                type="button"
                className="sp-btn sp-btn--primary"
                onClick={() => { setShowForm(!showForm); setShowGroupForm(false); }}
            >
                {showForm ? t('students:cancel') : t('students:addStudent')}
            </button>
        </>
    ) : null;

    if (loading) {
        return (
            <OwnerPageLayout title={coachView ? t('students:titleCoach') : t('students:title')} maxWidth={960}>
                <div className="sp-loading-page">{t('students:loadingStudents')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={coachView ? `👥 ${t('students:titleCoach')}` : `👥 ${t('students:title')}`}
            subtitle={pageSubtitle}
            actions={pageActions}
            maxWidth={960}
        >

                {/* Форма создания группы */}
                {!coachView && showGroupForm && (
                    <div className="sp-panel" style={{ borderColor: 'var(--sp-primary)', marginBottom: 20 }}>
                        <h3 className="sp-panel__title" style={{ marginBottom: 20 }}>
                            {editGroupId ? t('students:editGroup') : t('students:newGroup')}
                        </h3>
                        <form onSubmit={handleCreateGroup}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('students:groupNameLabel')}
                                    </label>
                                    <input
                                        value={groupForm.name}
                                        onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                                        placeholder={t('students:groupNamePlaceholder')}
                                        required style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('students:schedule')}
                                    </label>
                                    <input
                                        value={groupForm.scheduleJSON}
                                        onChange={e => setGroupForm({ ...groupForm, scheduleJSON: e.target.value })}
                                        placeholder={t('students:schedulePlaceholder')}
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                            </div>
                            <button type="submit" disabled={savingGroup} style={{
                                padding: '11px 28px',
                                background: savingGroup ? '#94a3b8' : '#EB3333',
                                color: 'white', border: 'none', borderRadius: '10px',
                                fontSize: '14px', fontWeight: '700', cursor: 'pointer'
                            }}>
                                {savingGroup ? t('students:savingGroup') : editGroupId ? t('students:saveGroupChanges') : t('students:createGroup')}
                            </button>
                        </form>
                    </div>
                )}

                {/* Форма добавления ученика */}
                {!coachView && showForm && (
                    <div style={{
                        background: 'white', borderRadius: '16px', padding: '24px',
                        marginBottom: '20px', border: '1px solid var(--sp-gray-100)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
                    }}>
                        <h3 style={{ margin: '0 0 20px', fontSize: '17px', fontWeight: '700', color: '#1A1A1A' }}>
                            {t('students:newStudent')}
                        </h3>
                        <form onSubmit={handleAddStudent}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('students:fullName')}
                                    </label>
                                    <input
                                        value={form.fullName}
                                        onChange={e => setForm({ ...form, fullName: e.target.value })}
                                        placeholder={t('students:fullNamePlaceholder')}
                                        required style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('students:birthDate')}
                                    </label>
                                    <input
                                        type="date" value={form.birthDate}
                                        onChange={e => setForm({ ...form, birthDate: e.target.value })}
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('students:studentPhone')}
                                    </label>
                                    <input
                                        value={form.phone}
                                        onChange={e => setForm({ ...form, phone: e.target.value })}
                                        placeholder={t('students:phonePlaceholder')}
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('students:studentEmail')}
                                    </label>
                                    <input
                                        type="email"
                                        value={form.contactEmail}
                                        onChange={e => setForm({ ...form, contactEmail: e.target.value })}
                                        placeholder="parent@example.com"
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('students:notes')}
                                    </label>
                                    <input
                                        value={form.medicalNote}
                                        onChange={e => setForm({ ...form, medicalNote: e.target.value })}
                                        placeholder={t('students:optionalPlaceholder')}
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                <div style={{ gridColumn: '1 / -1', padding: '14px', background: '#f8f9ff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>
                                        <input
                                            type="checkbox"
                                            checked={form.addGuardian}
                                            onChange={e => setForm({ ...form, addGuardian: e.target.checked })}
                                        />
                                        {t('students:linkGuardianNow')}
                                    </label>
                                    {form.addGuardian && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>
                                                    {t('students:whoGuardian')}
                                                </label>
                                                <GuardianRelationshipSelect
                                                    value={form.guardianRelationship}
                                                    onChange={v => setForm({ ...form, guardianRelationship: v })}
                                                    style={{ ...inputStyle, padding: '10px 12px' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>
                                                    {t('students:guardianPhone')}
                                                </label>
                                                <input
                                                    value={form.guardianPhone}
                                                    onChange={e => setForm({ ...form, guardianPhone: e.target.value })}
                                                    placeholder="+7..."
                                                    style={inputStyle}
                                                />
                                            </div>
                                            {guardianLookup?.found && (
                                                <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: '13px', color: '#059669' }}>
                                                    {t('students:guardianFound', { name: guardianLookup.displayName })}
                                                </p>
                                            )}
                                            {guardianLookup && !guardianLookup.found && form.guardianPhone.trim().length >= 10 && (
                                                <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: '13px', color: '#b45309' }}>
                                                    {t('students:guardianNotFound')}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button type="submit" style={{
                                padding: '11px 28px', background: '#1A1A1A',
                                color: 'white', border: 'none', borderRadius: '10px',
                                fontSize: '14px', fontWeight: '700', cursor: 'pointer'
                            }}>
                                {t('students:addStudentBtn')}
                            </button>
                        </form>
                    </div>
                )}

                {/* Группы — компактный список */}
                {groups.length > 0 && (
                    <div style={{
                        background: 'white', borderRadius: '14px', padding: '16px 20px',
                        marginBottom: '20px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        <div style={{
                            fontSize: '13px', fontWeight: '700', color: '#888',
                            marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em'
                        }}>
                            {t('students:groupsTitle')}
                        </div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                            {groups.map(g => (
                                <div key={g.id} style={{
                                    background: '#f8f9ff', borderRadius: '10px',
                                    padding: '10px 14px',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'space-between', gap: '10px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                                        <span style={{ fontSize: '18px' }}>🏷️</span>
                                        <div>
                                            <div style={{ fontWeight: '700', fontSize: '14px' }}>{g.name}</div>
                                            {g.scheduleJSON && (
                                                <div style={{ fontSize: '12px', color: '#888' }}>
                                                    📅 {g.scheduleJSON}
                                                </div>
                                            )}
                                        </div>
                                        <span style={{
                                            background: '#e8f5e9', color: '#2e7d32',
                                            padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600'
                                        }}>
                                            {g.studentCount} {t('students:studentsShort')}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setGroupForm({ name: g.name, scheduleJSON: g.scheduleJSON || '' });
                                            setEditGroupId(g.id);
                                            setShowGroupForm(true);
                                        }}
                                        style={{
                                            padding: '6px 14px', borderRadius: '8px',
                                            background: 'white', border: '1px solid var(--sp-gray-200)',
                                            cursor: 'pointer', fontSize: '12px', color: '#555'
                                        }}
                                    >
                                        {t('students:edit')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}


                {!coachView && (
                    <div style={{
                        display: 'inline-flex', background: '#fff', borderRadius: '10px',
                        padding: '4px', marginBottom: '16px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        <button
                            type="button"
                            onClick={() => setListTab('active')}
                            style={{
                                border: 'none', padding: '8px 18px', borderRadius: '8px',
                                cursor: 'pointer', fontWeight: 600, fontSize: '14px',
                                background: listTab === 'active' ? '#1A1A1A' : 'transparent',
                                color: listTab === 'active' ? '#fff' : '#64748b'
                            }}
                        >
                            {t('students:activeTab', { count: activeStudents.length })}
                        </button>
                        <button
                            type="button"
                            onClick={() => setListTab('archive')}
                            style={{
                                border: 'none', padding: '8px 18px', borderRadius: '8px',
                                cursor: 'pointer', fontWeight: 600, fontSize: '14px',
                                background: listTab === 'archive' ? '#1A1A1A' : 'transparent',
                                color: listTab === 'archive' ? '#fff' : '#64748b'
                            }}
                        >
                            {t('students:archiveTab', { count: archivedStudents.length })}
                        </button>
                    </div>
                )}

                {/* Поиск */}
                <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <span style={{
                        position: 'absolute', left: '14px', top: '50%',
                        transform: 'translateY(-50%)', fontSize: '16px'
                    }}>🔍</span>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('students:searchPlaceholder')}
                        style={{
                            ...inputStyle,
                            paddingLeft: '40px',
                            background: 'white'
                        }}
                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                    />
                </div>

                {/* Список учеников */}
                {filtered.length === 0 ? (
                    <div style={{
                        textAlign: 'center', padding: '48px 20px',
                        color: '#aaa', fontSize: '15px',
                        background: 'white', borderRadius: '14px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>👥</div>
                        {search
                            ? t('students:notFound')
                            : listTab === 'archive'
                                ? t('students:archiveEmpty')
                                : t('students:listEmpty')}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {filtered.map(s => {
                            const isArchived = s.status === 'archived';
                            return (
                            <div key={s.id} style={{
                                background: isArchived ? '#f9fafb' : 'white', borderRadius: '14px',
                                padding: '16px 20px', border: `1px solid ${isArchived ? '#e5e7eb' : 'var(--sp-gray-100)'}`,
                                opacity: isArchived ? 0.85 : 1,
                                transition: 'box-shadow 0.15s'
                            }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        {/* Имя */}
                                        <div style={{ fontWeight: '700', fontSize: '16px', color: '#1A1A1A', marginBottom: '6px' }}>
                                            {s.fullName}
                                            {isArchived && (
                                                <span style={{
                                                    marginLeft: '8px', fontSize: '11px', fontWeight: 600,
                                                    background: '#f3f4f6', color: '#6b7280',
                                                    padding: '2px 8px', borderRadius: '8px'
                                                }}>{t('students:inArchive')}</span>
                                            )}
                                        </div>
                                        {/* Контакт и дата */}
                                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                                            {(s.phone || s.parentPhone) && (
                                                <span>📱 {s.phone || s.parentPhone}</span>
                                            )}
                                            {s.birthDate && (
                                                <span>🎂 {formatDate(s.birthDate)}</span>
                                            )}
                                        </div>
                                        {(s.guardians || []).length > 0 && (
                                            <div style={{ fontSize: '12px', color: '#5b21b6', marginBottom: '8px' }}>
                                                👤 {(s.guardians || []).map(g =>
                                                    `${g.relationshipLabel || guardianRelationshipLabel(g.relationship)}${g.guardianPhone ? ` · ${g.guardianPhone}` : ''}`
                                                ).join(' · ')}
                                            </div>
                                        )}
                                        {/* Бейджи */}
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                            {s.activeGroup ? (
                                                <span style={{
                                                    background: '#e8f5e9', color: '#2e7d32',
                                                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600'
                                                }}>
                                                    🏷️ {s.activeGroup}
                                                </span>
                                            ) : (
                                                <span style={{
                                                    background: '#fce4ec', color: '#c62828',
                                                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600'
                                                }}>
                                                    {t('students:notEnrolled')}
                                                </span>
                                            )}
                                            {s.lessonsLeft != null && (
                                                <span style={{
                                                    background: s.lessonsLeft > 2 ? '#e3f2fd' : '#fff3e0',
                                                    color: s.lessonsLeft > 2 ? '#EB3333' : '#e65100',
                                                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600'
                                                }}>
                                                    {t('students:lessonsLeft', { count: s.lessonsLeft })}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Кнопка действий */}
                                    {!coachView && (
                                    <button
                                        onClick={() => setSelected(selected?.id === s.id ? null : s)}
                                        style={{
                                            padding: '8px 16px', borderRadius: '8px',
                                            border: `1.5px solid ${selected?.id === s.id ? '#EB3333' : 'var(--sp-gray-200)'}`,
                                            background: selected?.id === s.id ? '#f0ebff' : 'white',
                                            color: selected?.id === s.id ? '#EB3333' : '#666',
                                            cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                                            flexShrink: 0, marginLeft: '12px'
                                        }}
                                    >
                                        {selected?.id === s.id ? t('students:actionsClose') : t('students:actionsOpen')}
                                    </button>
                                    )}
                                </div>

                                {!coachView && selected?.id === s.id && (
                                    <div style={{
                                        marginTop: '14px', paddingTop: '14px',
                                        borderTop: '1px solid #f0f0f0'
                                    }}>
                                        <div style={{
                                            fontSize: '12px', fontWeight: '600', color: '#888',
                                            marginBottom: '10px', textTransform: 'uppercase'
                                        }}>
                                            {t('students:studentActions')}
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <select
                                                value={enrollGroupId}
                                                onChange={e => setEnrollGroupId(e.target.value)}
                                                style={{
                                                    padding: '8px 12px', borderRadius: '8px',
                                                    border: '1.5px solid var(--sp-gray-200)', fontSize: '13px',
                                                    outline: 'none', background: 'white'
                                                }}
                                            >
                                                <option value="">{t('students:pickGroupSelect')}</option>
                                                {groups.map(g => (
                                                    <option key={g.id} value={g.id}>{g.name}</option>
                                                ))}
                                            </select>

                                            <button onClick={() => handleEnroll(s.id)} style={{
                                                padding: '8px 16px', borderRadius: '8px',
                                                background: '#2ecc71', color: 'white',
                                                border: 'none', cursor: 'pointer',
                                                fontSize: '13px', fontWeight: '600'
                                            }}>
                                                {t('students:enroll')}
                                            </button>

                                            {!isArchived && (
                                                <>
                                                    <button onClick={() => handleDismiss(s.id)} style={{
                                                        padding: '8px 16px', borderRadius: '8px',
                                                        background: '#fce4ec', color: '#c62828',
                                                        border: '1px solid #ffcdd2', cursor: 'pointer',
                                                        fontSize: '13px', fontWeight: '600'
                                                    }}>
                                                        {t('students:expel')}
                                                    </button>
                                                    <button onClick={() => handleArchive(s.id, s.fullName)} style={{
                                                        padding: '8px 16px', borderRadius: '8px',
                                                        background: '#f3f4f6', color: '#6b7280',
                                                        border: '1px solid #e5e7eb', cursor: 'pointer',
                                                        fontSize: '13px', fontWeight: '600'
                                                    }}>
                                                        {t('students:toArchive')}
                                                    </button>
                                                </>
                                            )}
                                            {isArchived && (
                                                <button onClick={() => handleRestore(s.id)} style={{
                                                    padding: '8px 16px', borderRadius: '8px',
                                                    background: '#ecfdf5', color: '#059669',
                                                    border: '1px solid #a7f3d0', cursor: 'pointer',
                                                    fontSize: '13px', fontWeight: '600'
                                                }}>
                                                    {t('students:restore')}
                                                </button>
                                            )}

                                            {!isArchived && (
                                                <button
                                                    type="button"
                                                    onClick={() => setLinkGuardianFor({ id: s.id, name: s.fullName })}
                                                    style={{
                                                        padding: '8px 16px', borderRadius: '8px',
                                                        background: '#f0ebff', color: '#5b21b6',
                                                        border: '1px solid #ddd6fe', cursor: 'pointer',
                                                        fontSize: '13px', fontWeight: '600'
                                                    }}
                                                >
                                                    {t('students:guardian')}
                                                </button>
                                            )}
                                            {canManageSubscriptions() && (
                                                <a
                                                    href={`/subscriptions?studentId=${s.id}&name=${encodeURIComponent(s.fullName)}`}
                                                    style={{
                                                        padding: '8px 16px', borderRadius: '8px',
                                                        background: '#e3f2fd', color: '#EB3333',
                                                        border: '1px solid #bbdefb',
                                                        fontSize: '13px', fontWeight: '600',
                                                        textDecoration: 'none', display: 'inline-block'
                                                    }}
                                                >
                                                    {t('students:subscription')}
                                                </a>
                                            )}

                                            <a
                                                href="/attendance"
                                                style={{
                                                    padding: '8px 16px', borderRadius: '8px',
                                                    background: '#f3e5f5', color: '#7b1fa2',
                                                    border: '1px solid #e1bee7',
                                                    fontSize: '13px', fontWeight: '600',
                                                    textDecoration: 'none', display: 'inline-block'
                                                }}
                                            >
                                                {t('students:attendanceLink')}
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );})}
                    </div>
                )}
            {linkGuardianFor && (
                <LinkGuardianModal
                    studentId={linkGuardianFor.id}
                    studentName={linkGuardianFor.name}
                    onClose={() => setLinkGuardianFor(null)}
                    onLinked={loadData}
                />
            )}
        </OwnerPageLayout>
    );
}